/*
 * PROD-7828 W3 — Invoices: pure response shaping, the overview's invoice column and the "Awaiting
 * invoice" list, file naming and error routing, pinned to teacher_invoices.json by adapters.test.js.
 * The ops list carries no PAN or GSTIN; only the PDF does, and the server audits each download.
 */
import { EM_DASH, api_error, fmt, needs_review, num } from '../format';
import { STATUS_ALL, STATUS_CONFIRMED, STATUS_SUPERSEDED } from './requests';

const str = (v) => (typeof v === 'string' ? v : '');
const money = (v) => (typeof v === 'number' ? fmt(v, 2) : EM_DASH);
const pct = (v) => (str(v) ? `${str(v)}%` : EM_DASH);

export const VIEW_AWAITING = 'AWAITING';

export const VIEWS = [
  { value: STATUS_CONFIRMED, label: 'Confirmed' },
  { value: STATUS_SUPERSEDED, label: 'Replaced' },
  { value: STATUS_ALL, label: 'All' },
  { value: VIEW_AWAITING, label: 'Awaiting invoice' }
];

export const FORBIDDEN_TEXT = 'Only the teacher payments team can see invoices.';

const STATUSES = {
  CONFIRMED: { label: 'Confirmed', tone: 'green' },
  SUPERSEDED: { label: 'Replaced', tone: 'gray' }
};

export const invoice_status_view = (code) =>
  STATUSES[code] || { label: str(code) || EM_DASH, tone: 'gray' };

// GET /invoices/list: id order; next_after_id null = the last page.
export const list_view = (data) => {
  const d = data || {};
  const rows = (Array.isArray(d.rows) ? d.rows : []).map((r) => ({
    invoice_uuid: str(r.invoice_uuid),
    teacher_uuid: str(r.teacher_uuid),
    teacher_name: str(r.teacher_name) || EM_DASH,
    invoice_number: str(r.invoice_number) || EM_DASH,
    version: typeof r.close_version_no === 'number' ? `v${r.close_version_no}` : EM_DASH,
    status: invoice_status_view(r.status),
    taxable: money(r.taxable_amount),
    gst: `${money(r.gst_amount)} (${pct(r.gst_rate_pct)})`,
    invoice_total: money(r.invoice_total),
    tds: `${money(r.tds_amount)} (${pct(r.tds_rate_pct)})`,
    payable: money(r.payable_amount),
    confirmed_at: str(r.confirmed_at_ist) || EM_DASH,
    superseded_at: str(r.superseded_at_ist)
  }));
  const next = typeof d.next_after_id === 'number' && d.next_after_id > 0 ? d.next_after_id : null;
  return { rows, next_after_id: next };
};

/*
 * The overview row's invoice cell. The invoice comes after close, so an open month has none yet; a
 * closed month with nothing to pay never gets one.
 */
export const l1_invoice_cell = (row) => {
  const r = row || {};
  if (r.lifecycle !== 'CLOSED') return { label: 'After close', tone: 'gray', awaiting: false };
  if (!needs_review(r) && num(r.payable_amount) <= 0) {
    return { label: 'None (nothing to pay)', tone: 'gray', awaiting: false };
  }
  if (r.invoice_status === 'RECEIVED')
    return { label: 'Confirmed', tone: 'green', awaiting: false };
  return { label: 'Awaiting invoice', tone: 'orange', awaiting: true };
};

// One page of the closed-month overview rows, kept to those still awaiting the teacher's invoice.
export const awaiting_view = (data, offset = 0) => {
  const d = data || {};
  const rows = Array.isArray(d.rows) ? d.rows : [];
  const count = typeof d.count === 'number' ? d.count : rows.length;
  return {
    rows: rows
      .filter((r) => l1_invoice_cell(r).awaiting)
      .map((r) => ({
        teacher_uuid: str(r.teacher_uuid),
        teacher_name: str(r.teacher_name) || EM_DASH,
        teacher_email: str(r.teacher_email),
        payable: needs_review(r) ? 'Needs review' : money(r.payable_amount),
        reopened: num(r.reopen_count) > 0
      })),
    next_offset: offset + rows.length < count ? offset + rows.length : null
  };
};

// The server's Content-Disposition name when readable, else the same rule (invoicedoc.FileName).
export const invoice_pdf_name = (invoice_number, header_name = '') => {
  if (header_name) return header_name;
  const safe = str(invoice_number)
    .replace(/\//g, '-')
    .replace(/[^A-Za-z0-9._-]/g, '');
  return `MyYogaTeacher-invoice-${safe || 'unknown'}.pdf`;
};

// kind: 'auth' (401) | 'forbidden' (403: this tab only) | 'inline'.
export const invoice_error = (e, fallback) => {
  const { code, message } = api_error(e);
  const status = e && e.response && e.response.status;
  if (status === 401) return { kind: 'auth', text: message || fallback };
  if (status === 403 || code === 'FORBIDDEN') {
    return { kind: 'forbidden', text: message || FORBIDDEN_TEXT };
  }
  return { kind: 'inline', text: message || fallback };
};
